import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import type {
  APIResponse,
  Brand,
  PurchaseOrder,
  SKU,
  PermitMOH,
  PermitMOT,
  Payment,
  PaymentDashboard,
  PaymentPoSummary,
  Shipment,
  DashboardSummary,
  PipelineData,
  Reminder,
  ReminderCreate,
  LoginRequest,
  LoginResponse,
  User
} from '@/types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    // Remove Content-Type for FormData so axios can set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Add authorization header if token exists
    const authStorage = localStorage.getItem('enana-auth');
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch {
        // Invalid storage, ignore
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Sliding session renewal — if the backend returned a fresh token, store it.
    const refreshed = response.headers?.['x-refresh-token'];
    if (refreshed && typeof refreshed === 'string') {
      try {
        const raw = localStorage.getItem('enana-auth');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.state) {
            parsed.state.token = refreshed;
            localStorage.setItem('enana-auth', JSON.stringify(parsed));
          }
        }
      } catch {
        // ignore — next page reload will re-hydrate from store
      }
    }
    return response;
  },
  (error: AxiosError<APIResponse>) => {
    const message = error.response?.data?.error?.message || error.message || 'An error occurred';

    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      // Clear auth storage
      localStorage.removeItem('enana-auth');
      // Redirect to login if not already there
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Show error toast for non-404 errors
    if (error.response?.status !== 404) {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Auth API Methods
export const authApi = {
  login: (data: LoginRequest) => api.post<APIResponse<LoginResponse>>('/auth/login', data),
  me: () => api.get<APIResponse<User>>('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// API Methods
export const brandsApi = {
  list: () => api.get('/brands'),
  get: (id: string) => api.get(`/brands/${id}`),
  create: (data: unknown) => api.post('/brands', data),
  update: (id: string, data: unknown) => api.patch(`/brands/${id}`, data),
  delete: (id: string) => api.delete(`/brands/${id}`),
  getSkus: (id: string) => api.get(`/brands/${id}/skus`),
  getEstimatedDaysToTrebil: (id: string) => api.get(`/brands/${id}/estimated-days-to-trebil`),
};

export const posApi = {
  list: (params?: { brand_id?: string; status?: string; page?: number; page_size?: number }) =>
    api.get('/pos', { params }),
  get: (id: string) => api.get(`/pos/${id}`),
  create: (data: unknown) => api.post('/pos', data),
  update: (id: string, data: unknown) => api.patch(`/pos/${id}`, data),
  delete: (id: string) => api.delete(`/pos/${id}`),
  updateStatus: (id: string, status: string, notes?: string) =>
    api.patch(`/pos/${id}/status`, { status, notes }),
  getHistory: (id: string) => api.get(`/pos/${id}/history`),
  addItem: (id: string, item: unknown) => api.post(`/pos/${id}/items`, item),
  deleteItem: (poId: string, itemId: string) => api.delete(`/pos/${poId}/items/${itemId}`),
  getStatuses: () => api.get('/pos/statuses'),
  getNextNumber: (brandId: string) => api.get(`/pos/next-number/${brandId}`),
  validateShelfLife: (poId: string) => api.get(`/pos/${poId}/shelf-life`),
  updateItem: (poId: string, itemId: string, data: unknown) => api.patch(`/pos/${poId}/items/${itemId}`, data),
  proactiveShelfLifeCheck: (brandId: string, skuIds: string[]) =>
    api.post('/pos/shelf-life-check', { brand_id: brandId, sku_ids: skuIds }),
};

export const permitsApi = {
  listMOH: (params?: { brand_id?: string; status?: string; expiring_within_days?: number }) =>
    api.get('/permits/moh', { params }),
  getMOH: (id: string) => api.get(`/permits/moh/${id}`),
  createMOH: (data: unknown) => api.post('/permits/moh', data),
  createMOHWithFile: (formData: FormData) =>
    api.post('/permits/moh/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateMOH: (id: string, data: unknown) => api.patch(`/permits/moh/${id}`, data),
  deleteMOH: (id: string) => api.delete(`/permits/moh/${id}`),
  uploadMOHFile: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/permits/moh/${id}/upload-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  listMOT: (params?: { brand_id?: string; status?: string; expiring_within_days?: number }) =>
    api.get('/permits/mot', { params }),
  getMOT: (id: string) => api.get(`/permits/mot/${id}`),
  createMOT: (data: unknown) => api.post('/permits/mot', data),
  createMOTWithFile: (formData: FormData) =>
    api.post('/permits/mot/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateMOT: (id: string, data: unknown) => api.patch(`/permits/mot/${id}`, data),
  deleteMOT: (id: string) => api.delete(`/permits/mot/${id}`),
  uploadMOTFile: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/permits/mot/${id}/upload-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  checkCoverage: (poId: string) => api.get(`/permits/coverage/${poId}`),
  getSuggestions: (daysAhead?: number) => api.get('/permits/suggestions', { params: { days_ahead: daysAhead } }),
  allocate: (data: unknown) => api.post('/permits/allocate', data),
  getForBrand: (brandId: string, permitType?: 'moh' | 'mot') =>
    api.get(`/permits/for-brand/${brandId}`, { params: { permit_type: permitType } }),
  linkToPO: (poId: string, permitId: string, permitType: 'moh' | 'mot') =>
    api.post('/permits/link-to-po', null, { params: { po_id: poId, permit_id: permitId, permit_type: permitType } }),
  getAllocations: (poId: string) => api.get(`/permits/allocations/${poId}`),
  unallocate: (allocationId: string) => api.delete(`/permits/allocations/${allocationId}`),
  getMOHUsage: (permitId: string) => api.get(`/permits/moh/${permitId}/usage`),
  getMOTUsage: (permitId: string) => api.get(`/permits/mot/${permitId}/usage`),
  previewCoverage: (brandId: string, items: { sku_name: string; quantity: number }[]) =>
    api.post('/permits/preview-coverage', items, { params: { brand_id: brandId } }),

  // Permit extensions (per-item, document-backed, stackable)
  applyExtensions: (data: {
    moh_permit_id?: string | null;
    mot_permit_id?: string | null;
    items: Array<{
      moh_permit_item_id?: string | null;
      mot_permit_item_id?: string | null;
      moh_new_total_qty?: number | null;
      mot_new_total_qty?: number | null;
      sku_label?: string | null;
    }>;
    letter_number?: string | null;
    letter_date?: string | null;
    new_expiry_date?: string | null;
    document_id?: string | null;
    notes?: string | null;
  }) => api.post('/permits/extensions/apply', data),
  listMOHExtensions: (permitId: string) =>
    api.get(`/permits/moh/${permitId}/extensions`),
  listMOTExtensions: (permitId: string) =>
    api.get(`/permits/mot/${permitId}/extensions`),
  reverseExtension: (extensionId: string, reason: string, cascadePaired = true) =>
    api.post(`/permits/extensions/${extensionId}/reverse`, {
      reason,
      cascade_paired: cascadePaired,
    }),

  // Permit reconciliation (admin)
  previewReconcile: () => api.get('/permits/reconcile/preview'),
  applyReconcile: () => api.post('/permits/reconcile'),

  // Pre-system used baseline (paper-PO history typed when migrating into the system)
  setMOHItemBaseline: (
    itemId: string,
    newPreSystemUsedQty: number,
    reason?: string,
  ) =>
    api.patch(`/permits/moh/items/${itemId}/baseline`, {
      new_pre_system_used_qty: newPreSystemUsedQty,
      reason: reason ?? null,
    }),
  setMOTItemBaseline: (
    itemId: string,
    newPreSystemUsedQty: number,
    reason?: string,
  ) =>
    api.patch(`/permits/mot/items/${itemId}/baseline`, {
      new_pre_system_used_qty: newPreSystemUsedQty,
      reason: reason ?? null,
    }),

  // List all active extensions backing the permits used by a PO's allocations
  listExtensionsForPO: (poId: string) =>
    api.get(`/permits/po/${poId}/extensions`),

  // Export all permits to a styled Excel workbook (one sheet per brand)
  exportExcel: () =>
    api.get('/permits/export/excel', { responseType: 'blob', timeout: 120000 }),

  // AI: extract structured data from an MOH/MOT extension letter
  analyzeExtension: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/permits/extensions/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

export const invoicesApi = {
  list: (params?: { po_id?: string; brand_id?: string; invoice_type?: string }) =>
    api.get('/invoices', { params }),
  get: (id: string) => api.get(`/invoices/${id}`),
  create: (data: unknown) => api.post('/invoices', data),
  update: (id: string, data: unknown) => api.patch(`/invoices/${id}`, data),
  delete: (id: string) => api.delete(`/invoices/${id}`),
  getStats: (params?: { year?: number; brand_id?: string }) =>
    api.get('/invoices/stats/summary', { params }),
};

export const paymentsApi = {
  list: (params?: { po_id?: string; status?: string; due_within_days?: number; overdue_only?: boolean }) =>
    api.get('/payments', { params }),
  get: (id: string) => api.get(`/payments/${id}`),
  create: (data: unknown) => api.post('/payments', data),
  update: (id: string, data: unknown) => api.patch(`/payments/${id}`, data),
  markPaid: (id: string, data: unknown) => api.post(`/payments/${id}/mark-paid`, data),
  delete: (id: string) => api.delete(`/payments/${id}`),
  getSchedule: (poId: string) => api.get(`/payments/schedule/${poId}`),
  getStats: () => api.get('/payments/stats/summary'),
  // New dashboard endpoints
  getDashboard: () => api.get('/payments/dashboard'),
  getPoSummaries: () => api.get('/payments/po-summaries'),
  getPoSummary: (poId: string) => api.get(`/payments/po/${poId}/summary`),
  generateForPo: (poId: string, data?: { first_payment_due?: string; final_payment_due?: string }) =>
    api.post(`/payments/po/${poId}/generate`, data || {}),
};

export const shipmentsApi = {
  list: (params?: { po_id?: string; status?: string; arriving_within_days?: number }) =>
    api.get('/shipments', { params }),
  get: (id: string) => api.get(`/shipments/${id}`),
  create: (data: unknown) => api.post('/shipments', data),
  update: (id: string, data: unknown) => api.patch(`/shipments/${id}`, data),
  updateStatus: (id: string, status: string, actualDate?: string, notes?: string) =>
    api.patch(`/shipments/${id}/status`, { status, actual_date: actualDate, notes }),
  delete: (id: string) => api.delete(`/shipments/${id}`),
  getQuotes: (poId: string) => api.get(`/shipments/quotes/${poId}`),
  createQuote: (data: unknown) => api.post('/shipments/quotes', data),
  selectQuote: (quoteId: string) => api.patch(`/shipments/quotes/${quoteId}/select`),
  calculateEta: (data: { ship_date: string; shipping_days_to_aqaba: number; days_aqaba_to_trebel?: number }) =>
    api.post('/shipments/calculate-eta', data),
  getStats: () => api.get('/shipments/stats/summary'),
  getTrackingEvents: (id: string) => api.get(`/shipments/${id}/tracking-events`),
  createTrackingEvent: (id: string, data: unknown) => api.post(`/shipments/${id}/tracking-events`, data),
  registerTracking: (id: string) => api.post(`/shipments/${id}/register-tracking`),
  refreshTracking: (id: string) => api.post(`/shipments/${id}/refresh-tracking`),
  getRouteMap: (id: string) => api.get(`/shipments/${id}/route-map`),
  autoCreateFromBL: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/shipments/auto-create/from-bl', formData, { timeout: 120000 });
  },
  autoCreateFromContainer: (data: { container_number: string; po_id?: string }) =>
    api.post('/shipments/auto-create/from-container', data),
};

export const documentsApi = {
  list: (params?: { po_id?: string; document_type?: string }) =>
    api.get('/documents', { params }),
  get: (id: string) => api.get(`/documents/${id}`),
  create: (data: unknown) => api.post('/documents', data),
  update: (id: string, data: unknown) => api.patch(`/documents/${id}`, data),
  delete: (id: string) => api.delete(`/documents/${id}`),
  linkToPO: (docId: string, poId: string) => api.patch(`/documents/${docId}/link-po`, null, { params: { po_id: poId } }),
  upload: (data: FormData) => {
    // Content-Type is auto-handled by request interceptor for FormData
    return api.post('/documents/upload', data, {
      timeout: 120000, // 2 minutes for upload + AI analysis
    });
  },
  replace: (docId: string, data: FormData) => {
    // Replace document - deletes old from Drive and uploads new
    return api.post(`/documents/${docId}/replace`, data, {
      timeout: 120000, // 2 minutes for upload + AI analysis
    });
  },
  analyze: (id: string, analysisType?: string) =>
    api.post(`/documents/${id}/analyze`, null, { params: { analysis_type: analysisType } }),
  analyzeFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutes for AI analysis
    });
  },
  getTypes: () => api.get('/documents/types'),
  getStats: () => api.get('/documents/stats/summary'),
};

export const tasksApi = {
  list: (params?: { status?: string; category?: string; priority?: string; due_within_days?: number }) =>
    api.get('/tasks', { params }),
  get: (id: string) => api.get(`/tasks/${id}`),
  create: (data: unknown) => api.post('/tasks', data),
  update: (id: string, data: unknown) => api.patch(`/tasks/${id}`, data),
  updateStatus: (id: string, status: string, snoozedUntil?: string) =>
    api.patch(`/tasks/${id}/status`, { status, snoozed_until: snoozedUntil }),
  complete: (id: string) => api.post(`/tasks/${id}/complete`),
  snooze: (id: string, hours?: number) => api.post(`/tasks/${id}/snooze`, null, { params: { hours } }),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  getCategories: () => api.get('/tasks/categories/list'),
  getStats: () => api.get('/tasks/stats/summary'),
};

export const alertsApi = {
  list: (params?: { status?: string; severity?: string; alert_type?: string }) =>
    api.get('/alerts', { params }),
  get: (id: string) => api.get(`/alerts/${id}`),
  create: (data: unknown) => api.post('/alerts', data),
  acknowledge: (id: string) => api.post(`/alerts/${id}/acknowledge`),
  resolve: (id: string) => api.post(`/alerts/${id}/resolve`),
  dismiss: (id: string) => api.post(`/alerts/${id}/dismiss`),
  delete: (id: string) => api.delete(`/alerts/${id}`),
  acknowledgeAll: (severity?: string) => api.post('/alerts/acknowledge-all', null, { params: { severity } }),
  getTypes: () => api.get('/alerts/types'),
  getStats: () => api.get('/alerts/stats/summary'),
};

export const forecastsApi = {
  list: (params?: { year?: number; brand_id?: string; sku_id?: string }) =>
    api.get('/forecasts', { params }),
  get: (id: string) => api.get(`/forecasts/${id}`),
  create: (data: unknown) => api.post('/forecasts', data),
  update: (id: string, data: unknown) => api.patch(`/forecasts/${id}`, data),
  delete: (id: string) => api.delete(`/forecasts/${id}`),
  getAnalysis: (year: number) => api.get(`/forecasts/analysis/${year}`),
  getByBrand: (brandId: string, year: number) => api.get(`/forecasts/by-brand/${brandId}/${year}`),
  getPOSuggestions: (year: number, quarter?: string, brandId?: string) =>
    api.get(`/forecasts/suggestions/po/${year}`, { params: { quarter, brand_id: brandId } }),
  getPermitRequirements: (year: number, brandId?: string) =>
    api.get(`/forecasts/suggestions/permits/${year}`, { params: { brand_id: brandId } }),
};

export const skusApi = {
  list: (params?: { brand_id?: string; active?: boolean; search?: string }) =>
    api.get('/skus', { params }),
  get: (id: string) => api.get(`/skus/${id}`),
  create: (data: unknown) => api.post('/skus', data),
  update: (id: string, data: unknown) => api.patch(`/skus/${id}`, data),
  delete: (id: string) => api.delete(`/skus/${id}`),
  activate: (id: string) => api.post(`/skus/${id}/activate`),
  getPermitCoverage: (id: string) => api.get(`/skus/${id}/permit-coverage`),
  bulkCreate: (data: unknown) => api.post('/skus/bulk', data),
  getStats: () => api.get('/skus/stats/summary'),
};

export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
  getPipeline: () => api.get('/dashboard/pipeline'),
  getUpcomingPayments: (limit?: number) => api.get('/dashboard/upcoming-payments', { params: { limit } }),
  getShipmentsInTransit: () => api.get('/dashboard/shipments-in-transit'),
  getAlerts: (limit?: number) => api.get('/dashboard/alerts', { params: { limit } }),
  getReminders: (limit?: number) => api.get('/dashboard/reminders', { params: { limit } }),
  getTimeline: (daysAhead?: number) => api.get('/dashboard/timeline', { params: { days_ahead: daysAhead } }),
  getAlertSummary: () => api.get('/dashboard/alert-summary'),
  getNotificationHistory: (limit?: number) => api.get('/dashboard/notification-history', { params: { limit } }),
};

export const notificationsApi = {
  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data: unknown) => api.patch('/notifications/preferences', data),

  // Rules
  getRules: (params?: { brand_id?: string; enabled_only?: boolean }) =>
    api.get('/notifications/rules', { params }),
  createRule: (data: unknown) => api.post('/notifications/rules', data),
  updateRule: (id: string, data: unknown) => api.patch(`/notifications/rules/${id}`, data),
  deleteRule: (id: string) => api.delete(`/notifications/rules/${id}`),

  // Push subscriptions
  subscribe: (data: { endpoint: string; p256dh: string; auth: string; user_agent?: string }) =>
    api.post('/notifications/subscribe', data),
  unsubscribe: (endpoint: string) => api.delete('/notifications/unsubscribe', { params: { endpoint } }),
  testPush: () => api.post('/notifications/test-push'),

  // History
  getHistory: (params?: { channel?: string; status?: string; limit?: number; skip?: number }) =>
    api.get('/notifications/history', { params }),

  // Alert snooze
  snoozeAlert: (id: string, data: { duration: string; custom_hours?: number }) =>
    api.post(`/alerts/${id}/snooze`, data),
};

export const predictionsApi = {
  getLeadTime: (brandCode: string) => api.get(`/predictions/lead-time/${brandCode}`),
  getPermitDepletion: (permitId: string) => api.get(`/predictions/permit-depletion/${permitId}`),
  getCashFlow: (months?: number) => api.get('/predictions/cash-flow', { params: { months } }),
  getContainerFit: (data: { brand_id: string; items: { sku_id: string; quantity: number }[] }) =>
    api.post('/predictions/container-fit', data),
};

export const remindersApi = {
  list: (po_id: string) => api.get('/reminders', { params: { po_id } }),
  create: (data: unknown) => api.post('/reminders', data),
  update: (id: string, data: unknown) => api.patch(`/reminders/${id}`, data),
  delete: (id: string) => api.delete(`/reminders/${id}`),
};

export const usersApi = {
  list: () => api.get('/users'),
  create: (data: { email: string; password: string; full_name?: string; full_name_ar?: string; role: string; whatsapp_number?: string }) =>
    api.post('/users', data),
  update: (id: string, data: unknown) => api.patch(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

export const notesApi = {
  list: (params?: { po_id?: string; requires_action?: boolean; limit?: number }) =>
    api.get('/notes', { params }),
  create: (data: { po_id: string; shipment_id?: string; content: string; priority?: string; requires_action?: boolean }) =>
    api.post('/notes', data),
  update: (id: string, data: unknown) => api.patch(`/notes/${id}`, data),
  delete: (id: string) => api.delete(`/notes/${id}`),
};

export const managerApi = {
  getSummary: () => api.get('/manager/summary'),
  getOrders: (params?: { status?: string; brand_id?: string }) =>
    api.get('/manager/orders', { params }),
  getOrderDetail: (id: string) => api.get(`/manager/orders/${id}`),
  getShipments: () => api.get('/manager/shipments'),
  getAlerts: () => api.get('/manager/alerts'),
  getBrands: () => api.get('/manager/brands'),
};

export const regulatoryApi = {
  getSummary: () => api.get('/regulatory/summary'),
  getTasks: (params?: { status?: string; task_type?: string; po_id?: string }) =>
    api.get('/regulatory/tasks', { params }),
  createTask: (data: any) => api.post('/regulatory/tasks', data),
  updateTask: (id: string, data: any) => api.patch(`/regulatory/tasks/${id}`, data),
  getPermits: () => api.get('/regulatory/permits'),
  startPermitApplication: (permitId: string) =>
    api.patch(`/regulatory/permits/moh/${permitId}/start-application`),
  uploadPermitFile: (permitType: string, permitId: string, data: any) =>
    api.post(`/regulatory/permits/${permitType}/${permitId}/upload`, data),
  extendPermitQty: (permitId: string) =>
    api.patch(`/regulatory/permits/moh/${permitId}/extend-qty`),
  extendPermitExpiry: (permitId: string) =>
    api.patch(`/regulatory/permits/moh/${permitId}/extend-expiry`),
  getCustoms: () => api.get('/regulatory/customs'),
  createCustoms: (data: any) => api.post('/regulatory/customs', data),
  advanceCustoms: (id: string) => api.patch(`/regulatory/customs/${id}/advance`),
  uploadCustomsDoc: (id: string, data: any) =>
    api.post(`/regulatory/customs/${id}/upload`, data),
  getShipments: () => api.get('/regulatory/shipments'),
  getReminders: () => api.get('/regulatory/reminders'),
  createReminder: (data: any) => api.post('/regulatory/reminders', data),
  updateReminder: (id: string, data: any) => api.patch(`/regulatory/reminders/${id}`, data),
  // Checklists (admin)
  getChecklists: (params?: { status?: string }) =>
    api.get('/checklists/pre-arrival', { params }),
  updateChecklist: (id: string, data: any) =>
    api.patch(`/checklists/pre-arrival/${id}`, data),
};

export const telegramApi = {
  getPending: () => api.get('/telegram/pending'),
  processPending: (id: string, data: { document_type?: string; po_id?: string }) =>
    api.post(`/telegram/pending/${id}/process`, data),
  dismissPending: (id: string) => api.delete(`/telegram/pending/${id}`),
};

// Helper to extract data from backend response format {success, data, message}
function extractData<T>(response: AxiosResponse<APIResponse<T>>): T {
  return response.data.data;
}

// Type definitions for API client methods
interface BrandsAPI {
  list: () => Promise<Brand[]>;
  get: (id: string) => Promise<Brand>;
  create: (data: Partial<Brand>) => Promise<Brand>;
  update: (id: string, data: Partial<Brand>) => Promise<Brand>;
  delete: (id: string) => Promise<void>;
}

interface PurchaseOrdersAPI {
  list: (params?: { brand_id?: string; status?: string; page?: number; page_size?: number }) => Promise<PurchaseOrder[]>;
  get: (id: string) => Promise<PurchaseOrder>;
  create: (data: unknown) => Promise<PurchaseOrder>;
  update: (id: string, data: unknown) => Promise<PurchaseOrder>;
}

interface SkusAPI {
  list: (params?: { brand_id?: string; active?: boolean; search?: string }) => Promise<SKU[]>;
  get: (id: string) => Promise<SKU>;
  create: (data: Partial<SKU>) => Promise<SKU>;
  update: (id: string, data: Partial<SKU>) => Promise<SKU>;
  delete: (id: string) => Promise<void>;
}

interface PermitsAPI {
  listMOH: (params?: { brand_id?: string; status?: string; expiring_within_days?: number }) => Promise<PermitMOH[]>;
  listMOT: (params?: { brand_id?: string; status?: string; expiring_within_days?: number }) => Promise<PermitMOT[]>;
  get: (id: string) => Promise<PermitMOH>;
  create: (data: unknown) => Promise<PermitMOH>;
  update: (id: string, data: unknown) => Promise<PermitMOH>;
}

interface PaymentsAPI {
  list: (params?: { po_id?: string; status?: string; due_within_days?: number; overdue_only?: boolean }) => Promise<Payment[]>;
  get: (id: string) => Promise<Payment>;
  create: (data: Partial<Payment>) => Promise<Payment>;
  update: (id: string, data: Partial<Payment>) => Promise<Payment>;
  markPaid: (id: string, data: { paid_date: string }) => Promise<Payment>;
  getDashboard: () => Promise<PaymentDashboard>;
  getPoSummaries: () => Promise<PaymentPoSummary[]>;
  getPoSummary: (poId: string) => Promise<PaymentPoSummary>;
  generateForPo: (poId: string, data?: { first_payment_due?: string; final_payment_due?: string }) => Promise<{ created_count: number; payments: Payment[] }>;
}

interface ShipmentsAPI {
  list: (params?: { po_id?: string; status?: string; arriving_within_days?: number }) => Promise<Shipment[]>;
  get: (id: string) => Promise<Shipment>;
  create: (data: Partial<Shipment>) => Promise<Shipment>;
  update: (id: string, data: Partial<Shipment>) => Promise<Shipment>;
}

interface NotificationsAPI {
  list: () => Promise<never[]>;
  markRead: (id: string) => Promise<null>;
  markAllRead: () => Promise<null>;
  dismiss: (id: string) => Promise<null>;
}

interface DashboardAPI {
  getSummary: () => Promise<DashboardSummary>;
  getPipeline: () => Promise<PipelineData>;
}

interface RemindersAPI {
  list: (po_id: string) => Promise<Reminder[]>;
  create: (data: ReminderCreate) => Promise<Reminder>;
  update: (id: string, data: Partial<Reminder>) => Promise<Reminder>;
  delete: (id: string) => Promise<void>;
}

interface ApiClient {
  brands: BrandsAPI;
  purchaseOrders: PurchaseOrdersAPI;
  skus: SkusAPI;
  permits: PermitsAPI;
  payments: PaymentsAPI;
  shipments: ShipmentsAPI;
  notifications: NotificationsAPI;
  dashboard: DashboardAPI;
  reminders: RemindersAPI;
}

// Unified API object for components
export const apiClient: ApiClient = {
  brands: {
    list: async (): Promise<Brand[]> => extractData(await brandsApi.list()),
    get: async (id: string): Promise<Brand> => extractData(await brandsApi.get(id)),
    create: async (data: Partial<Brand>): Promise<Brand> => extractData(await brandsApi.create(data)),
    update: async (id: string, data: Partial<Brand>): Promise<Brand> => extractData(await brandsApi.update(id, data)),
    delete: async (id: string): Promise<void> => extractData(await brandsApi.delete(id)),
  },
  purchaseOrders: {
    list: async (params?: Parameters<typeof posApi.list>[0]): Promise<PurchaseOrder[]> => {
      const response = await posApi.list(params);
      const data = response.data.data;
      // Backend returns paginated response { items: [], total, page, ... }
      return data?.items || data || [];
    },
    get: async (id: string): Promise<PurchaseOrder> => extractData(await posApi.get(id)),
    create: async (data: unknown): Promise<PurchaseOrder> => extractData(await posApi.create(data)),
    update: async (id: string, data: unknown): Promise<PurchaseOrder> => extractData(await posApi.update(id, data)),
  },
  skus: {
    list: async (params?: Parameters<typeof skusApi.list>[0]): Promise<SKU[]> => extractData(await skusApi.list(params)),
    get: async (id: string): Promise<SKU> => extractData(await skusApi.get(id)),
    create: async (data: Partial<SKU>): Promise<SKU> => extractData(await skusApi.create(data)),
    update: async (id: string, data: Partial<SKU>): Promise<SKU> => extractData(await skusApi.update(id, data)),
    delete: async (id: string): Promise<void> => extractData(await skusApi.delete(id)),
  },
  permits: {
    listMOH: async (params?: Parameters<typeof permitsApi.listMOH>[0]): Promise<PermitMOH[]> => extractData(await permitsApi.listMOH(params)),
    listMOT: async (params?: Parameters<typeof permitsApi.listMOT>[0]): Promise<PermitMOT[]> => extractData(await permitsApi.listMOT(params)),
    get: async (id: string): Promise<PermitMOH> => extractData(await permitsApi.getMOH(id)),
    create: async (data: unknown): Promise<PermitMOH> => extractData(await permitsApi.createMOH(data)),
    update: async (id: string, data: unknown): Promise<PermitMOH> => extractData(await permitsApi.updateMOH(id, data)),
  },
  payments: {
    list: async (params?: Parameters<typeof paymentsApi.list>[0]): Promise<Payment[]> => extractData(await paymentsApi.list(params)),
    get: async (id: string): Promise<Payment> => extractData(await paymentsApi.get(id)),
    create: async (data: Partial<Payment>): Promise<Payment> => extractData(await paymentsApi.create(data)),
    update: async (id: string, data: Partial<Payment>): Promise<Payment> => extractData(await paymentsApi.update(id, data)),
    markPaid: async (id: string, data: { paid_date: string }): Promise<Payment> => extractData(await paymentsApi.markPaid(id, data)),
    getDashboard: async (): Promise<PaymentDashboard> => extractData(await paymentsApi.getDashboard()),
    getPoSummaries: async (): Promise<PaymentPoSummary[]> => extractData(await paymentsApi.getPoSummaries()),
    getPoSummary: async (poId: string): Promise<PaymentPoSummary> => extractData(await paymentsApi.getPoSummary(poId)),
    generateForPo: async (poId: string, data?: { first_payment_due?: string; final_payment_due?: string }): Promise<{ created_count: number; payments: Payment[] }> => extractData(await paymentsApi.generateForPo(poId, data)),
  },
  shipments: {
    list: async (params?: Parameters<typeof shipmentsApi.list>[0]): Promise<Shipment[]> => extractData(await shipmentsApi.list(params)),
    get: async (id: string): Promise<Shipment> => extractData(await shipmentsApi.get(id)),
    create: async (data: Partial<Shipment>): Promise<Shipment> => extractData(await shipmentsApi.create(data)),
    update: async (id: string, data: Partial<Shipment>): Promise<Shipment> => extractData(await shipmentsApi.update(id, data)),
  },
  notifications: {
    list: async (): Promise<never[]> => [], // Notifications endpoint not implemented yet
    markRead: async (_id: string): Promise<null> => null,
    markAllRead: async (): Promise<null> => null,
    dismiss: async (_id: string): Promise<null> => null,
  },
  dashboard: {
    getSummary: async (): Promise<DashboardSummary> => extractData(await dashboardApi.getSummary()),
    getPipeline: async (): Promise<PipelineData> => extractData(await dashboardApi.getPipeline()),
  },
  reminders: {
    list: async (po_id: string): Promise<Reminder[]> => extractData(await remindersApi.list(po_id)),
    create: async (data: ReminderCreate): Promise<Reminder> => extractData(await remindersApi.create(data)),
    update: async (id: string, data: Partial<Reminder>): Promise<Reminder> => extractData(await remindersApi.update(id, data)),
    delete: async (id: string): Promise<void> => extractData(await remindersApi.delete(id)),
  },
};

// Export individual APIs for direct use
export { api };

// Export unified API as default
export default apiClient;
